import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { bannerList } from "@/mocks/banners";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const count = bannerList.length;

  const goTo = useCallback((next: number) => setIndex(((next % count) + count) % count), [count]);

  useEffect(() => {
    const timer = setInterval(() => setIndex((v) => (v + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[460px] w-full sm:h-[520px] md:h-[600px]">
        {bannerList.map((banner, i) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={banner.image}
              alt={`${banner.title} 焦点海报`}
              title={`${banner.title} 影视作品焦点图`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-transparent" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] flex-col justify-end px-4 pb-12 md:px-6 md:pb-16">
          <div key={index} className="animate-fade-up max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-semibold text-primary-950">
              <i className="ri-fire-line text-sm" />
              本周推荐
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              {bannerList[index].title}
            </h1>
            <p className="mt-3 text-sm font-medium text-accent-300 md:text-base">
              {bannerList[index].tagline}
            </p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-100/85 md:text-base">
              {bannerList[index].summary}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${bannerList[index].mediaId}`}
                className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold whitespace-nowrap text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-fill text-lg" />
                立即观看
              </Link>
              <Link
                to="/category/movie"
                className="inline-flex items-center gap-2 rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-semibold whitespace-nowrap text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
              >
                <i className="ri-movie-line text-lg" />
                浏览全部影片
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 right-4 z-10 flex items-center gap-2 md:right-6">
          {bannerList.map((banner, i) => (
            <button
              key={banner.id}
              type="button"
              aria-label={`切换到 ${banner.title}`}
              onClick={() => goTo(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(index - 1)}
          className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/30 md:flex"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(index + 1)}
          className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/30 md:flex"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>
      </div>
    </section>
  );
}